import { OrganizationUnitType, UserType, VenueBranch } from '@inplay/models';
import {
  GroupedBranchType,
  GroupedOrganizationType,
  GroupedSubOrganizationType,
  GroupedVenueType,
} from '../../service/context/organizationContext.types';
import CollapseModal from '../collapseModal/collapseModal';
import Search from '../search/search';
import DropdownModal from '../dropdownModal/dropdownModal';
import { useEffect, useMemo, useState } from 'react';
import {
  expandHierarchy,
  VenueDropdownProps,
  SearchResults,
  RenderCollapseHierarchyProps,
  HierarchyLevelAndItems,
  VenueHierarchyLevel,
} from './pageHeader.type';

export const HighlightMatch = ({
  text,
  query,
}: {
  text: string;
  query: string;
}): JSX.Element => {
  if (!query) return <>{text}</>;

  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedQuery})`, 'gi');
  const parts = text.split(regex);

  return (
    <span>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} style={{ backgroundColor: 'yellow' }}>
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </span>
  );
};

/**
 * Retrieves search results by filtering grouped organization data based on a search query.
 *
 * @param groupedOrganizationData - The hierarchical organization data which can be one of
 * GroupedOrganizationType, GroupedSubOrganizationType, or GroupedVenueType.
 * @param searchQuery - The search query string used to filter the organization data.
 * @returns An object containing arrays of IDs for matched organizations, sub-organizations,
 * venues, and venue branches.
 *
 * The function performs a case-insensitive search on the organization hierarchy, identifying
 * all entities whose names include the searchQuery string. It collects and returns the IDs
 * of these matching entities, organizing them into respective categories.
 */

export const getSearchResults = (
  groupedOrganizationData:
    | GroupedOrganizationType
    | GroupedSubOrganizationType
    | GroupedVenueType
    | GroupedBranchType
    | undefined,
  searchQuery: string,
): SearchResults => {
  if (!searchQuery || searchQuery.trim() === '' || !groupedOrganizationData) {
    return {
      organization: [],
      subOrganization: [],
      venue: [],
      venueBranches: [],
    };
  }

  const lowerQuery: string = searchQuery.toLowerCase();

  const matchedOrganizations = new Set<string>();
  const matchedSubOrganizations = new Set<string>();
  const matchedVenues = new Set<string>();
  const matchedVenueBranches = new Set<string>();

  const isMatch = (name: string) => name.toLowerCase().includes(lowerQuery);

  const addVenueBranchHierarchy = (branch: VenueBranch & { _id: string }) => {
    matchedVenueBranches.add(branch._id);
    matchedOrganizations.add(branch.organizationId);
    branch.subOrganizationIds.forEach((subOrgId) =>
      matchedSubOrganizations.add(subOrgId),
    );
    matchedVenues.add(branch.venueId);
  };

  const addVenueHierarchy = (venue: GroupedVenueType) => {
    matchedVenues.add(venue._id);
    matchedOrganizations.add(venue.organizationId);
    venue.subOrganizationIds.forEach((subOrgId) =>
      matchedSubOrganizations.add(subOrgId),
    );
  };

  const addSubOrgHierarchy = (subOrg: GroupedSubOrganizationType) => {
    matchedSubOrganizations.add(subOrg._id);
    matchedOrganizations.add(subOrg.organizationId);
  };

  if ('suborganizations' in groupedOrganizationData) {
    if (isMatch(groupedOrganizationData.name)) {
      matchedOrganizations.add(groupedOrganizationData._id);
    }

    groupedOrganizationData.suborganizations.forEach((subOrg) => {
      if (isMatch(subOrg.name)) {
        addSubOrgHierarchy(subOrg);
      }

      subOrg.venues.forEach((venue) => {
        if (isMatch(venue.name)) {
          addVenueHierarchy(venue);
        }

        venue.venueBranches.forEach((branch) => {
          if (isMatch(branch.name)) {
            addVenueBranchHierarchy(branch);
          }
        });
      });
    });
  } else if ('venues' in groupedOrganizationData) {
    if (isMatch(groupedOrganizationData.name)) {
      addSubOrgHierarchy(groupedOrganizationData);
    }

    groupedOrganizationData.venues.forEach((venue) => {
      if (isMatch(venue.name)) {
        addVenueHierarchy(venue);
      }

      venue.venueBranches.forEach((branch) => {
        if (isMatch(branch.name)) {
          addVenueBranchHierarchy(branch);
        }
      });
    });
  } else if ('venueBranches' in groupedOrganizationData) {
    if (isMatch(groupedOrganizationData.name)) {
      addVenueHierarchy(groupedOrganizationData);
    }

    groupedOrganizationData.venueBranches.forEach((branch) => {
      if (isMatch(branch.name)) {
        addVenueBranchHierarchy(branch);
      }
    });
  }

  return {
    organization: Array.from(matchedOrganizations),
    subOrganization: Array.from(matchedSubOrganizations),
    venue: Array.from(matchedVenues),
    venueBranches: Array.from(matchedVenueBranches),
  };
};

/**
 * Recursively renders a collapsible hierarchy of organizations, sub-organizations, venues and venue branches.
 * @param level One of 'organization', 'subOrganization', 'venue'
 * @param items The array of items to render at the current level
 * @param expandHierarchy An object with properties for each level, set to the id of the item to expand
 * @param searchResults An object with properties for each level, set to an array of ids of items that matched the search
 * @param searchQuery The search query to highlight matches for
 * @param onBranchSelect A function to call when a branch is selected
 * @param props.selectedVenueBranch The currently selected venue branch
 * @returns A JSX.Element array of collapsible items
 */
export const renderCollapseHierarchy = ({
  level,
  items,
  expandHierarchy,
  searchResults,
  searchQuery,
  onBranchSelect,
  selectedVenueBranch,
}: RenderCollapseHierarchyProps): JSX.Element[] => {
  return items
    .map((item: any) => {
      const id = item._id;
      const name = item.name;

      let expand = false;
      if (level === VenueHierarchyLevel.ORGANIZATION) {
        expand =
          expandHierarchy.organization === id ||
          searchResults.organization.includes(id);
      } else if (level === VenueHierarchyLevel.SUB_ORGANIZATION) {
        expand =
          expandHierarchy.subOrganization === id ||
          searchResults.subOrganization.includes(id);
      } else if (level === VenueHierarchyLevel.VENUE) {
        expand =
          expandHierarchy.venue === id || searchResults.venue.includes(id);
      }

      if (level === VenueHierarchyLevel.VENUE_BRANCH) {
        return (
          <div
            key={id}
            className={`px-3 py-2 w-full cursor-pointer branch-item ${
              selectedVenueBranch?.name === name ? 'selected-branch' : ''
            }`}
            onClick={() => onBranchSelect(item)}
          >
            <HighlightMatch text={name} query={searchQuery} />
            {selectedVenueBranch?.name === name && <span>&#10003;</span>}
          </div>
        );
      }

      if ('venueBranches' in item) {
        // venue level
        return (
          <CollapseModal
            key={id}
            headerContent={<HighlightMatch text={name} query={searchQuery} />}
            expand={expand}
          >
            {item.venueBranches.map((branch: VenueBranch & { _id: string }) => (
              <div
                key={branch._id}
                className={`ml-6 px-3 py-1 w-full cursor-pointer branch-item ${
                  selectedVenueBranch?.name === branch.name
                    ? 'selected-branch'
                    : ''
                }`}
                onClick={() => onBranchSelect(branch)}
              >
                <HighlightMatch text={branch.name} query={searchQuery} />
                {selectedVenueBranch?.name === branch.name && (
                  <span>&#10003;</span>
                )}
              </div>
            ))}
          </CollapseModal>
        );
      }

      if ('venues' in item) {
        // subOrganization level
        return (
          <CollapseModal
            key={id}
            headerContent={<HighlightMatch text={name} query={searchQuery} />}
            expand={expand}
          >
            {renderCollapseHierarchy({
              level: VenueHierarchyLevel.VENUE,
              items: item.venues,
              expandHierarchy,
              searchResults,
              searchQuery,
              onBranchSelect,
              selectedVenueBranch,
            })}
          </CollapseModal>
        );
      }

      if ('suborganizations' in item) {
        // organization level
        return (
          <CollapseModal
            key={id}
            headerContent={<HighlightMatch text={name} query={searchQuery} />}
            expand={expand}
          >
            {renderCollapseHierarchy({
              level: VenueHierarchyLevel.SUB_ORGANIZATION,
              items: item.suborganizations,
              expandHierarchy,
              searchResults,
              searchQuery,
              onBranchSelect,
              selectedVenueBranch,
            })}
          </CollapseModal>
        );
      }

      return null; // unknown structure, skip
    })
    .filter(Boolean) as JSX.Element[]; // ensures only JSX.Element[] is returned
};

export const VenueDropdown = (props: VenueDropdownProps) => {
  const [openVenueDropdown, setOpenVenueDropdown] = useState<boolean>(false);
  const [expandHierarchy, setExpandHierarchy] = useState<expandHierarchy>({
    organization: undefined,
    subOrganization: undefined,
    venue: undefined,
  });
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    if (props.selectedVenueBranch) {
      setExpandHierarchy({
        organization: props.selectedVenueBranch.organizationId,
        subOrganization: props.selectedVenueBranch.subOrganizationIds[0],
        venue: props.selectedVenueBranch.venueId,
      });
    }
  }, [props.selectedVenueBranch]);

  const searchResults = useMemo(() => {
    return getSearchResults(props.groupedOrganizationData, searchQuery);
  }, [props.groupedOrganizationData, searchQuery]);

  if (!props.decodedLoginData || !props.groupedOrganizationData) {
    return null;
  }

  const getInitialLevelAndItems = (): HierarchyLevelAndItems => {
    const userType = props.decodedLoginData!.type;
    const orgData = props.groupedOrganizationData!;

    if (
      userType === UserType.ORGANIZATION_ADMIN &&
      'suborganizations' in orgData
    ) {
      return {
        level: VenueHierarchyLevel.SUB_ORGANIZATION,
        items: orgData.suborganizations,
      };
    }

    if (userType === UserType.SUB_ORGANIZATION_ADMIN) {
      const firstSubOrg: GroupedSubOrganizationType =
        orgData as GroupedSubOrganizationType;
      return {
        level: VenueHierarchyLevel.VENUE,
        items: firstSubOrg?.venues ?? [],
      };
    }

    if ('venueBranches' in orgData) {
      return {
        level: VenueHierarchyLevel.VENUE,
        items: [orgData],
      };
    }

    if (
      (userType === UserType.VENUE_BRANCH_LOGIN ||
        userType === UserType.VENUE_STAFF) &&
      orgData.type === OrganizationUnitType.VENUE_BRANCH
    ) {
      return {
        level: VenueHierarchyLevel.VENUE_BRANCH,
        items: [orgData],
      };
    }

    throw new Error('Invalid groupedOrganizationData structure for user type');
  };

  return (
    <DropdownModal
      label={props.selectedVenueBranch?.name ?? 'Select Venue'}
      isOpen={openVenueDropdown}
      handleClick={() => setOpenVenueDropdown(true)}
      handleClose={() => {
        setOpenVenueDropdown(false);
        setSearchQuery('');
      }}
      isDisabled={!props.groupedOrganizationData}
      contentClassName="max-h-[400px] overflow-y-scroll"
    >
      <Search
        query={searchQuery}
        onChange={(value: string) => setSearchQuery(value.toLowerCase())}
        placeholder="Search"
        containerClass="search-container"
        inputClass="search-input"
        iconSize={10}
      />

      {renderCollapseHierarchy({
        ...getInitialLevelAndItems(),
        expandHierarchy,
        searchResults,
        searchQuery,
        onBranchSelect: (branch) => {
          props.updateSelectedVenueBranch(branch);
          setOpenVenueDropdown(false);
        },
        selectedVenueBranch: props.selectedVenueBranch,
      })}
    </DropdownModal>
  );
};
